import { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from "react";
import { supabase, fromDb, withTimeout, DB, warn, log, authFetch } from "@/lib/supabase";
import { SEED_TEAM_MEMBERS, DEFAULT_PERMISSIONS, DEFAULT_APP_SETTINGS, DEV_VERSION, SEED_PRODUCTS, SEED_PROGRAMS } from "@/lib/constants";
import { canAccess, ErrorBoundary, avatarColor } from "@/lib/helpers";
import { MemberAvatar } from "@/components";
import { useDataMerge } from "@/hooks/useDataMerge";
import type { Product, Program, TeamMember, GeneratedContent, QAItem, AppSettings, ChannelCopy } from "@/lib/types";
import type { Session } from "@supabase/supabase-js";

// Pipeline loads eagerly (default landing page)
import { PipelinePage } from "@/pages/PipelinePage";

// Lazy import with auto-retry — handles stale chunks after deploys
function lazyRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(() =>
    factory().catch(() => {
      // First attempt failed (stale chunk) — reload from server
      window.location.reload();
      // Return a never-resolving promise so React doesn't render the error
      return new Promise<{ default: T }>(() => {});
    })
  );
}

// All other pages lazy-loaded — only fetched when navigated to
const ProgramsPage = lazyRetry(() => import("@/pages/ProgramsPage").then(m => ({ default: m.ProgramsPage })));
const ProgramDetail = lazyRetry(() => import("@/pages/ProgramDetail").then(m => ({ default: m.ProgramDetail })));
const ProductsPage = lazyRetry(() => import("@/pages/ProductsPage").then(m => ({ default: m.ProductsPage })));
const CopyGeneratePage = lazyRetry(() => import("@/pages/AIGenerate/CopyGeneratePage").then(m => ({ default: m.CopyGeneratePage })));
const ImageStudioPage = lazyRetry(() => import("@/pages/AIGenerate/ImageStudioPage").then(m => ({ default: m.ImageStudioPage })));
const QAReviewPage = lazyRetry(() => import("@/pages/QAReviewPage").then(m => ({ default: m.QAReviewPage })));
const SettingsPage = lazyRetry(() => import("@/pages/SettingsPage").then(m => ({ default: m.SettingsPage })));
const CopyManagerPage = lazyRetry(() => import("@/pages/CopyManagerPage").then(m => ({ default: m.CopyManagerPage })));
const UserManagementPage = lazyRetry(() => import("@/pages/UserManagementPage").then(m => ({ default: m.UserManagementPage })));

interface CurrentUser {
  id: string;
  email: string;
  name: string;
  role: string;
  title: string;
  avatarUrl?: string;
}

interface PXMDashProps {
  onLogout: () => void;
  onUpdateName?: (name: string) => Promise<void>;
  currentUser: CurrentUser | null;
  session: Session;
}

interface GlobalMsg {
  text: string;
  type: "error" | "success" | "warn";
}

export function PXMDash({ onLogout, currentUser, session }: PXMDashProps) {
  const [activeNav, setActiveNavRaw] = useState<string>(() => {
    const hash = window.location.hash.replace("#", "").split("/")[0];
    return hash || "pipeline";
  });

  const setActiveNav = (nav: string) => {
    setActiveNavRaw(nav);
    const subHash = window.location.hash.includes("/") ? "/" + window.location.hash.split("/").slice(1).join("/") : "";
    if (nav === "pipeline") {
      history.replaceState(null, "", window.location.pathname);
    } else {
      window.location.hash = nav + (nav === "studio" ? subHash : "");
    }
  };

  // Sync nav from browser back/forward buttons
  useEffect(() => {
    const onHash = () => {
      const hash = window.location.hash.replace("#", "").split("/")[0];
      setActiveNavRaw(hash || "pipeline");
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const [permissions, setPermissions] = useState<Record<string, Record<string, string>>>(DEFAULT_PERMISSIONS as Record<string, Record<string, string>>);

  const userRole = currentUser?.role || "viewer";
  const [selectedProgramId, setSelectedProgramId] = useState<string | number | null>(null);
  const [programDetailOrigin, setProgramDetailOrigin] = useState<string>("programs");
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("pxm_sidebar_collapsed") === "true");
  const [darkMode, setDarkMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deferredLoading, setDeferredLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data state
  const [products, setProducts] = useState<Product[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [generatedContent, setGeneratedContent] = useState<GeneratedContent[]>([]);
  const [qaQueue, setQAQueue] = useState<QAItem[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [globalMsg, setGlobalMsg] = useState<GlobalMsg | null>(null);
  const [onlineUsers, setOnlineUsers] = useState<Record<string, { lastSeen: number; name: string }>>({});

  const showMsg = (text: string, type: "error" | "success" | "warn" = "error", duration = 5000) => {
    setGlobalMsg({ text, type });
    setTimeout(() => setGlobalMsg(null), duration);
  };

  // Pagination state
  const [totalProductCount, setTotalProductCount] = useState(0);
  const [productsPage, setProductsPage] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const allProductsLoaded = products.length >= totalProductCount && totalProductCount > 0;

  // Data merge helpers
  const { mergeCopyOntoProducts, mergeArtDirectionOntoProducts } = useDataMerge();

  const loadMoreProducts = useCallback(async () => {
    if (loadingMore || allProductsLoaded) return;
    setLoadingMore(true);
    const nextPage = productsPage + 1;
    try {
      const more = await DB.loadProductsPage(nextPage, DB.PAGE_SIZE);
      if (more && more.length > 0) {
        setProducts(prev => {
          const existingIds = new Set(prev.map(p => p.id));
          let newOnes = more.filter((p: Product) => !existingIds.has(p.id));
          // Merge cached deferred data onto new products
          if (deferredCopyRef.current) newOnes = mergeCopyOntoProducts(newOnes, deferredCopyRef.current);
          if (deferredArtDirectionRef.current) newOnes = mergeArtDirectionOntoProducts(newOnes, deferredArtDirectionRef.current);
          return [...prev, ...newOnes];
        });
        setProductsPage(nextPage);
      }
    } catch (err) {
      console.error("loadMoreProducts error:", err);
    }
    setLoadingMore(false);
  }, [productsPage, loadingMore, allProductsLoaded, mergeCopyOntoProducts, mergeArtDirectionOntoProducts]);

  // Session-level cache for AI Generate state
  const genImagesCacheRef = useRef<Record<string, any>>({});
  const aiSessionRef = useRef<any>(null);

  // Cache deferred data so loadMoreProducts can merge it onto new pages
  const deferredCopyRef = useRef<any[] | null>(null);
  const deferredArtDirectionRef = useRef<any[] | null>(null);

  // ─── App Settings (stored in Supabase) ───
  const [appSettings, setAppSettings] = useState<AppSettings>({ ...DEFAULT_APP_SETTINGS } as AppSettings);
  const settingsSaveTimer = useRef<NodeJS.Timeout | null>(null);
  const settingsLoaded = useRef(false);
  const dataLoaded = useRef(false);
  const [settingsSaveStatus, setSettingsSaveStatus] = useState<"idle" | "pending" | "saving" | "saved">("idle");

  useEffect(() => {
    if (!settingsLoaded.current) return;
    if (settingsSaveTimer.current) clearTimeout(settingsSaveTimer.current);
    setSettingsSaveStatus("pending");
    settingsSaveTimer.current = setTimeout(() => {
      setSettingsSaveStatus("saving");
      DB.saveSettings(appSettings).then(() => {
        log("[AppSettings] Saved to Supabase");
        setSettingsSaveStatus("saved");
        setTimeout(() => setSettingsSaveStatus("idle"), 2000);
      });
    }, 1500);
    return () => {
      if (settingsSaveTimer.current) clearTimeout(settingsSaveTimer.current);
    };
  }, [appSettings]);

  const updateSettings = (updates: Partial<AppSettings>) => {
    setAppSettings(prev => ({ ...prev, ...updates }));
  };

  // Sync permissions from appSettings (Supabase) — this is the source of truth for all users
  useEffect(() => {
    const perms = (appSettings as any).permissions;
    if (perms && Object.keys(perms).length > 0) {
      setPermissions(perms);
      localStorage.setItem("pxm_permissions", JSON.stringify(perms));
    }
  }, [appSettings]);

  // Load data on mount
  useEffect(() => {
    if (dataLoaded.current) return;
    dataLoaded.current = true;

    // ── Phase 1: Critical path — products + programs (shows the shell) ──
    const loadCriticalData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [loadedProducts, productCount, loadedPrograms] = await Promise.all([
          DB.loadProductsPage(0, DB.PAGE_SIZE).catch(() => null),
          DB.countProducts().catch(() => 0),
          DB.loadPrograms().catch(() => null),
        ]);

        if (loadedProducts && loadedProducts.length > 0) {
          setProducts(loadedProducts);
          setTotalProductCount(productCount || loadedProducts.length);
          setProductsPage(0);
        } else if (DB.isConnected()) {
          await DB.seedData(SEED_PRODUCTS, SEED_PROGRAMS, SEED_TEAM_MEMBERS);
          const seedProducts = await DB.loadProductsPage(0, DB.PAGE_SIZE);
          const seedCount = await DB.countProducts();
          setProducts(seedProducts || SEED_PRODUCTS);
          setTotalProductCount(seedCount || SEED_PRODUCTS.length);
          setProductsPage(0);
        } else {
          setProducts(SEED_PRODUCTS as Product[]);
          setTotalProductCount(SEED_PRODUCTS.length);
        }

        setPrograms(loadedPrograms && loadedPrograms.length > 0 ? loadedPrograms : SEED_PROGRAMS as Program[]);
        setLoading(false);
      } catch (err) {
        console.error("Critical data load failed:", err);
        warn("Falling back to seed data (Supabase may be paused or unreachable)");
        setProducts(SEED_PRODUCTS as Product[]);
        setPrograms(SEED_PROGRAMS as Program[]);
        setTeamMembers(SEED_TEAM_MEMBERS as TeamMember[]);
        setTotalProductCount(SEED_PRODUCTS.length);
        settingsLoaded.current = true;
        setError(null);
        setLoading(false);
        setDeferredLoading(false);
      }
    };

    // ── Phase 2: Deferred — everything else streams in behind the UI ──
    const loadDeferredData = async () => {
      try {
        const [loadedTeam, loadedGenerated, loadedQA, loadedSettings, authTeam, loadedCopy, loadedArtDirection] = await Promise.all([
          DB.loadTeamMembers().catch(() => null),
          DB.loadGeneratedContent().catch(() => null),
          DB.loadQAQueue().catch(() => null),
          DB.loadSettings().catch(() => null),
          session?.access_token
            ? fetch("/api/auth-admin", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ action: "list-users", adminToken: session.access_token }),
              }).then(r => r.json()).catch(() => null)
            : Promise.resolve(null),
          DB.loadAllProductCopy().catch(() => null),
          DB.loadAllProductArtDirection().catch(() => null),
        ]);

        // Cache deferred data for loadMoreProducts to reuse
        if (loadedCopy && loadedCopy.length > 0) deferredCopyRef.current = loadedCopy;
        if (loadedArtDirection && loadedArtDirection.length > 0) deferredArtDirectionRef.current = loadedArtDirection;

        // Merge copy + art direction onto existing products in state
        if ((loadedCopy && loadedCopy.length > 0) || (loadedArtDirection && loadedArtDirection.length > 0)) {
          setProducts(prev => {
            let merged = prev;
            if (loadedCopy && loadedCopy.length > 0) merged = mergeCopyOntoProducts(merged, loadedCopy);
            if (loadedArtDirection && loadedArtDirection.length > 0) merged = mergeArtDirectionOntoProducts(merged, loadedArtDirection);
            return merged;
          });
        }

        // Team members — DB (team_members table) is the single source of truth
        // Auth admin only adds lastSignIn and ensures users exist
        let resolvedTeam: TeamMember[] = SEED_TEAM_MEMBERS as TeamMember[];
        if (loadedTeam && loadedTeam.length > 0) resolvedTeam = loadedTeam;
        if (authTeam?.users && authTeam.users.length > 0) {
          // Start with DB team members as the base
          const dbTeam = [...(loadedTeam || [])];
          // Add any auth users not in the DB, update lastSignIn for those that are
          for (const u of authTeam.users) {
            const existing = dbTeam.find((m: TeamMember) => m.email === u.email);
            if (existing) {
              // Update auth-only fields, keep everything else from DB
              existing.lastActive = u.lastSignIn ? new Date(u.lastSignIn).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : existing.lastActive;
              if (!existing.role || existing.role === "viewer") existing.role = u.role || existing.role;
            } else {
              // New user from auth not yet in team_members — add with defaults
              const defaultColor = avatarColor(u.name || u.email?.split("@")[0]);
              dbTeam.push({
                id: u.id,
                name: u.name || u.email?.split("@")[0],
                initials: (u.name || u.email || "U").split(" ").map((w: string) => w[0]).join("").toUpperCase().slice(0, 2),
                email: u.email,
                role: u.role || "viewer",
                title: u.title || "",
                color: defaultColor.bg,
                avatarUrl: u.avatarUrl || "",
                status: "offline",
                lastActive: u.lastSignIn ? new Date(u.lastSignIn).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "Never",
                programs: 0,
                products: 0,
              });
            }
          }
          resolvedTeam = dbTeam;
        }
        setTeamMembers(resolvedTeam);

        setGeneratedContent(loadedGenerated || []);
        setQAQueue(loadedQA || []);
        if (loadedSettings && Object.keys(loadedSettings).length > 0) {
          setAppSettings(prev => ({ ...prev, ...loadedSettings }));
        }
        settingsLoaded.current = true;
      } catch (err) {
        console.error("Deferred data load failed:", err);
        settingsLoaded.current = true;
      }
      setDeferredLoading(false);
    };

    // Fire critical first, then deferred immediately after (no await blocking)
    loadCriticalData().then(() => loadDeferredData());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Background cleanup: shorten long titles as products load ───
  // Quick deterministic trim: strip brand prefix + truncate at last comma/space
  const quickShortenTitle = (title: string): string => {
    let t = title
      .replace(/^U\s*Brands?\s*/i, "")
      .replace(/,\s*Whiteboard Pens.*/i, "")
      .replace(/,\s*Dry Erase Markers? For.*/i, "")
      .replace(/\s*with Chisel Point.*/i, "")
      .replace(/\s*with Chisel Tip.*/i, "")
      .replace(/\s*Chisel Tip Set,?\s*/i, "Chisel Tip ")
      .replace(/\s+/g, " ")
      .trim();
    if (t.length > TITLE_MAX) {
      t = t.substring(0, TITLE_MAX);
      const lastSpace = t.lastIndexOf(" ");
      if (lastSpace > TITLE_MAX * 0.6) t = t.substring(0, lastSpace);
      t = t.replace(/[,\s]+$/, "");
    }
    return t;
  };

  const titleCleanupProcessed = useRef<Set<string | number>>(new Set());
  const prevLoadingRef = useRef(true);
  useEffect(() => {
    // Only run when transitioning from loading → loaded (not on every products change)
    const wasLoading = prevLoadingRef.current;
    prevLoadingRef.current = loading || deferredLoading;
    if (loading || deferredLoading) return;
    if (!wasLoading) return; // Skip if we were already loaded

    const longTitles = products.filter(
      p => p.id && p.title && p.title.length > TITLE_MAX && !titleCleanupProcessed.current.has(p.id)
    );
    if (longTitles.length === 0) return;
    for (const p of longTitles) titleCleanupProcessed.current.add(p.id!);
    log(`[TitleCleanup] Shortening ${longTitles.length} titles > ${TITLE_MAX} chars`);
    (async () => {
      for (const product of longTitles) {
        // Try AI first, fall back to deterministic trim
        let newTitle = quickShortenTitle(product.title!);
        try {
          const res = await authFetch("/api/generate", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              mode: "shorten-title",
              product: { title: product.title, category: product.category, itemNumber: product.itemNumber },
            }),
          });
          const data = await res.json() as { success?: boolean; title?: string };
          if (data.success && data.title && data.title.length <= TITLE_MAX) {
            newTitle = data.title;
          }
        } catch {
          // AI failed — use deterministic result
        }
        if (newTitle !== product.title && newTitle.length <= TITLE_MAX) {
          const updated = { ...product, title: newTitle };
          await DB.saveProduct(updated);
          setProducts(prev => prev.map(p => p.id === product.id ? { ...p, title: newTitle } : p));
          log(`[TitleCleanup] "${product.title}" → "${newTitle}"`);
        }
      }
    })();
  }, [products, loading, deferredLoading]);

  // ─── Realtime Subscriptions ───
  const sessionUserId = session?.user?.id;
  useEffect(() => {
    if (!supabase || !sessionUserId) return;

    const channel = supabase.channel("pxm-realtime");

    // Products
    channel.on("postgres_changes", { event: "INSERT", schema: "public", table: "products" }, (payload: { new: Record<string, unknown>; old: Record<string, unknown> }) => {
      const newProduct = fromDb(payload.new);
      setProducts(prev => prev.some(p => p.id === newProduct.id) ? prev : [...prev, newProduct]);
      setTotalProductCount(prev => prev + 1);
    });
    channel.on("postgres_changes", { event: "UPDATE", schema: "public", table: "products" }, (payload: { new: Record<string, unknown>; old: Record<string, unknown> }) => {
      const updated = fromDb(payload.new);
      setProducts(prev => prev.map(p => p.id === updated.id ? updated : p));
    });
    channel.on("postgres_changes", { event: "DELETE", schema: "public", table: "products" }, (payload: { new: Record<string, unknown>; old: Record<string, unknown> }) => {
      const deletedId = payload.old.id;
      setProducts(prev => prev.filter(p => p.id !== deletedId));
      setTotalProductCount(prev => Math.max(0, prev - 1));
    });

    // Programs
    channel.on("postgres_changes", { event: "INSERT", schema: "public", table: "programs" }, (payload: { new: Record<string, unknown>; old: Record<string, unknown> }) => {
      const newProg = fromDb(payload.new);
      setPrograms(prev => prev.some(p => p.id === newProg.id) ? prev : [...prev, newProg]);
    });
    channel.on("postgres_changes", { event: "UPDATE", schema: "public", table: "programs" }, (payload: { new: Record<string, unknown>; old: Record<string, unknown> }) => {
      const updated = fromDb(payload.new);
      setPrograms(prev => prev.map(p => p.id === updated.id ? updated : p));
    });
    channel.on("postgres_changes", { event: "DELETE", schema: "public", table: "programs" }, (payload: { new: Record<string, unknown>; old: Record<string, unknown> }) => {
      setPrograms(prev => prev.filter(p => p.id !== payload.old.id));
    });

    // QA Queue
    channel.on("postgres_changes", { event: "INSERT", schema: "public", table: "qa_queue" }, (payload: { new: Record<string, unknown>; old: Record<string, unknown> }) => {
      const newItem = fromDb(payload.new);
      setQAQueue(prev => prev.some(q => q.id === newItem.id) ? prev : [newItem, ...prev]);
    });
    channel.on("postgres_changes", { event: "UPDATE", schema: "public", table: "qa_queue" }, (payload: { new: Record<string, unknown>; old: Record<string, unknown> }) => {
      const updated = fromDb(payload.new);
      setQAQueue(prev => prev.map(q => q.id === updated.id ? updated : q));
    });

    // Generated Content
    channel.on("postgres_changes", { event: "INSERT", schema: "public", table: "generated_content" }, (payload: { new: Record<string, unknown>; old: Record<string, unknown> }) => {
      const newContent = fromDb(payload.new);
      setGeneratedContent(prev => prev.some(g => g.id === newContent.id) ? prev : [...prev, newContent]);
    });
    channel.on("postgres_changes", { event: "UPDATE", schema: "public", table: "generated_content" }, (payload: { new: Record<string, unknown>; old: Record<string, unknown> }) => {
      const updated = fromDb(payload.new);
      setGeneratedContent(prev => prev.map(g => g.id === updated.id ? updated : g));
    });

    // Product Copy (normalized table)
    channel.on("postgres_changes", { event: "*", schema: "public", table: "product_copy" }, (payload: { new: Record<string, unknown>; old: Record<string, unknown> }) => {
      const row = payload.new;
      if (!row || !row.product_id || !row.channel) return;
      const copyEntry: ChannelCopy = {
        title: row.title as string | undefined || "",
        bullets: row.bullets as string[] | undefined || [],
        description: row.description as string | undefined || "",
        backendKeywords: row.backend_keywords as string | undefined || "",
        approvedAt: row.approved_at as string | undefined || "",
        approvedBy: row.approved_by as string | undefined || "",
      };
      setProducts(prev => prev.map(p => {
        if (p.id !== row.product_id) return p;
        const updatedCopy: Record<string, ChannelCopy> = { ...(p.approvedCopy || {}), [row.channel as string]: copyEntry };
        return { ...p, approvedCopy: updatedCopy };
      }));
    });

    channel.subscribe((status: string) => {
      log("[Realtime] Subscription status:", status);
    });

    // ─── Presence Channel ───
    const userEmail = session?.user?.email;
    const userName = session?.user?.user_metadata?.name || userEmail?.split("@")[0] || "Unknown";
    let presenceChannel: ReturnType<typeof supabase.channel> | null = null;

    if (userEmail) {
      presenceChannel = supabase.channel("pxm-presence", { config: { presence: { key: userEmail } } });
      presenceChannel.on("presence", { event: "sync" }, () => {
        const state = presenceChannel!.presenceState();
        const online: Record<string, { lastSeen: number; name: string }> = {};
        Object.entries(state).forEach(([email, presences]: [string, any[]]) => {
          if (presences.length > 0) online[email] = { lastSeen: Date.now(), name: presences[0].name };
        });
        setOnlineUsers(online);
      });
      presenceChannel.subscribe(async (status: string) => {
        if (status === "SUBSCRIBED") {
          await presenceChannel!.track({ name: userName, email: userEmail, online_at: new Date().toISOString() });
        }
      });
    }

    return () => {
      if (supabase) {
        supabase.removeChannel(channel);
        if (presenceChannel) supabase.removeChannel(presenceChannel);
      }
    };
  }, [sessionUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Mutation Handlers ───
  const TITLE_MAX = 60;

  const handleSaveProduct = async (product: Product) => {
    // Stamp current user on every save
    if (currentUser) {
      product = { ...product, updatedBy: currentUser.id, updatedByName: currentUser.name || currentUser.email };
    }
    // Auto-shorten long titles using AI
    if (product.title && product.title.length > TITLE_MAX) {
      try {
        const res = await authFetch("/api/generate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            mode: "shorten-title",
            product: { title: product.title, category: product.category, itemNumber: product.itemNumber },
          }),
        });
        const data = await res.json() as { success?: boolean; title?: string };
        if (data.success && data.title && data.title.length <= TITLE_MAX) {
          log(`[SaveProduct] Title shortened: "${product.title}" → "${data.title}"`);
          product = { ...product, title: data.title };
        }
      } catch (e) {
        warn("[SaveProduct] Title shortening failed, saving as-is:", (e as Error).message);
      }
    }
    try {
      const saved = await DB.saveProduct(product);
      if (saved) {
        // Write-through: keep normalized art direction table in sync
        if (product.artDirection?.images) {
          DB.saveProductArtDirection(String(product.id), product.artDirection).catch(() => {});
        }
        // Fire-and-forget: log activity
        DB.logActivity(
          String(saved.id),
          "edit",
          currentUser?.id,
          currentUser?.name || currentUser?.email,
        ).catch(() => {});
        log("[SaveProduct] DB save OK. approvedCopy keys:", Object.keys(saved.approvedCopy || {}));
        // Preserve artDirection from the incoming product (JSONB column strips generatedImages on round-trip)
        const merged = saved.artDirection?.images ? saved : { ...saved, artDirection: product.artDirection };
        setProducts(prev => prev.some(p => p.id === merged.id) ? prev.map(p => p.id === merged.id ? merged : p) : [...prev, merged]);
        return;
      }
      warn("[SaveProduct] DB returned null — using optimistic update");
      showMsg("Changes saved locally — database sync pending", "warn");
    } catch (e: unknown) {
      warn("[SaveProduct] failed:", (e as Error).message);
      showMsg("Couldn't reach database — changes saved locally only", "warn");
    }
    setProducts(prev => prev.some(p => p.id === product.id) ? prev.map(p => p.id === product.id ? product : p) : [...prev, product]);
  };

  const handleDeleteProduct = async (id: string | number | undefined) => {
    setProducts(prev => prev.filter(p => p.id !== id));
    if (id != null) try { await DB.deleteProduct(String(id)); } catch (e: unknown) { warn("deleteProduct failed:", (e as Error).message); }
  };

  const handleCreateProgram = async (program: Partial<Program>) => {
    const newProgram = { ...program, id: program.id || Math.floor(Math.random() * 2000000000) + 1, productCount: 0, stage: "brief", status: "On Track" } as Program;
    try {
      const saved = await DB.saveProgram(newProgram);
      if (saved) { setPrograms(prev => [...prev, saved]); return; }
    } catch (e: unknown) { warn("createProgram failed:", (e as Error).message); }
    setPrograms(prev => [...prev, newProgram]);
  };

  const handleUpdateProgram = async (program: Program) => {
    try {
      const saved = await DB.saveProgram(program);
      if (saved) { setPrograms(prev => prev.map(p => p.id === saved.id ? saved : p)); return; }
    } catch (e: unknown) { warn("saveProgram failed:", (e as Error).message); }
    setPrograms(prev => prev.map(p => p.id === program.id ? program : p));
  };

  const handleUpdateProgramStage = async (id: string | number | undefined, stage: string) => {
    setPrograms(prev => prev.map(p => p.id === id ? { ...p, stage } : p));
    if (id != null) try { await DB.updateProgramStage(String(id), stage); } catch (e: unknown) { warn("updateProgramStage failed:", (e as Error).message); }
  };

  const handleDeleteProgram = async (id: string | number | undefined) => {
    const programProducts = products.filter(p => String(p.programId) === String(id));
    const msg = programProducts.length > 0
      ? `Delete this program and its ${programProducts.length} product${programProducts.length > 1 ? "s" : ""}? This cannot be undone.`
      : "Delete this program?";
    if (!confirm(msg)) return;
    setPrograms(prev => prev.filter(p => p.id !== id));
    if (programProducts.length > 0) {
      setProducts(prev => prev.filter(p => String(p.programId) !== String(id)));
      try {
        if (supabase) {
          for (const prod of programProducts) {
            const pid = String(prod.id);
            // Delete child rows first to avoid foreign key conflicts
            await withTimeout(supabase.from("generated_content").delete().eq("product_id", pid)).catch(() => {});
            await withTimeout(supabase.from("qa_queue").delete().eq("product_id", pid)).catch(() => {});
            await withTimeout(supabase.from("product_copy").delete().eq("product_id", pid)).catch(() => {});
            await withTimeout(supabase.from("product_art_direction").delete().eq("product_id", pid)).catch(() => {});
            await withTimeout(supabase.from("approved_images").delete().eq("product_id", pid)).catch(() => {});
            await withTimeout(supabase.from("products").delete().eq("id", prod.id));
          }
        }
      } catch (e: unknown) { warn("deleteProgram products cleanup failed:", (e as Error).message); }
    }
    try {
      if (supabase) await withTimeout(supabase.from("programs").delete().eq("id", id));
    } catch (e: unknown) { warn("deleteProgram failed:", (e as Error).message); }
  };

  const handleSaveGenerated = async (content: GeneratedContent, qaItem?: QAItem) => {
    setGeneratedContent(prev => [...prev, content]);
    if (qaItem) {
      setQAQueue(prev => [...prev, qaItem]);
    }
  };

  const handleUpdateQA = async (id: string | number | undefined, status: string, notes: string | null) => {
    setQAQueue(prev => prev.map(q => q.id === id ? { ...q, status, notes: notes || "" } : q));
    if (id != null) try { await DB.updateQAStatus(String(id), status, notes || ""); } catch (e: unknown) { warn("updateQA failed:", (e as Error).message); }
  };

  const handleDeleteQA = async (id: string | number | undefined) => {
    if (id == null) return;
    setQAQueue(prev => prev.filter(q => q.id !== id));
    try { await DB.deleteQAItem(String(id)); } catch (e: unknown) { warn("deleteQA failed:", (e as Error).message); }
  };

  const handleApproveAndStoreQA = async (qaItem: QAItem, product: Product, genData: any) => {
    log("[ApproveStore] Starting. Channel:", qaItem.channel);
    if (qaItem.id != null) await handleUpdateQA(qaItem.id, "approved", null);
    const channel = (qaItem.channel || "").toLowerCase().replace(/\s+/g, "");
    if (!channel) {
      console.error("[ApproveStore] No channel found on QA item!");
      return product;
    }
    const copyEntry = {
      title: genData?.title || "",
      bullets: genData?.bullets || [],
      description: genData?.description || "",
      backendKeywords: genData?.backendKeywords || "",
      approvedAt: new Date().toISOString(),
      approvedBy: currentUser?.name || currentUser?.email || "Unknown",
    };
    const existingCopy = product.approvedCopy || {};
    const updatedApprovedCopy = { ...existingCopy, [channel]: copyEntry };
    const updatedProduct = { ...product, approvedCopy: updatedApprovedCopy };
    await handleSaveProduct(updatedProduct);
    try { if (product.id != null) await DB.saveProductCopy(String(product.id), channel, copyEntry); } catch (e: unknown) { warn("[ApproveStore] product_copy dual-write skipped:", (e as Error).message); }
    // Log copy approval activity
    if (product.id != null) {
      DB.logActivity(String(product.id), "copy_approved", currentUser?.id, currentUser?.name || currentUser?.email, { channel }).catch(() => {});
    }
    setProducts(prev => prev.map(p => p.id === updatedProduct.id ? updatedProduct : p));
    return updatedProduct;
  };

  const handleSaveTeamMember = async (member: TeamMember) => {
    const matchMember = (m: TeamMember, target: TeamMember) =>
      m.id === target.id || (m.email && m.email === target.email);
    try {
      const saved = await DB.saveTeamMember(member);
      if (saved) {
        setTeamMembers(prev => prev.some(m => matchMember(m, saved)) ? prev.map(m => matchMember(m, saved) ? { ...m, ...saved } : m) : [...prev, saved]);
        return;
      }
    } catch (e: unknown) { warn("saveTeamMember failed:", (e as Error).message); }
    setTeamMembers(prev => prev.some(m => matchMember(m, member)) ? prev.map(m => matchMember(m, member) ? { ...m, ...member } : m) : [...prev, member]);
  };

  // Compute live product counts from actual product→program associations
  const programsWithCounts = useMemo(() =>
    programs.map(prog => ({
      ...prog,
      productCount: products.filter(p => String(p.programId) === String(prog.id)).length,
    })),
    [programs, products]
  );

  // Loading screen
  if (loading) {
    return (
      <div className="flex-center bg-bg h-full-vh">
        <div className="text-center">
          <img src="/branding/pxmdash-logo-stacked.png?v=2" alt="PXM Dash" className="loading-logo" />
          <div className="text-base text-light ff-secondary">Loading your workspace...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-center bg-bg h-full-vh">
        <div className="text-center max-w-400">
          <div className="fw-700 ff-primary text-dark mb-5 text-4xl">Error Loading Data</div>
          <div className="text-base ff-secondary text-light mb-10">{error}</div>
          <button onClick={() => window.location.reload()} className="btn btn-primary pad-2xl">Retry</button>
        </div>
      </div>
    );
  }

  const openProgram = (id: string | number | undefined) => {
    if (id != null) setSelectedProgramId(id);
    setProgramDetailOrigin(activeNav);
    setActiveNav("program-detail");
  };

  const selectedProgram = programsWithCounts.find(p => String(p.id) === String(selectedProgramId));

  const renderContent = () => {
    if (activeNav === "program-detail" && selectedProgram) {
      return (
        <ErrorBoundary>
          <ProgramDetail
            program={selectedProgram}
            products={products}
            teamMembers={teamMembers}
            currentUserEmail={currentUser?.email || ""}
            backLabel={programDetailOrigin === "pipeline" ? "Pipeline" : programDetailOrigin === "products" ? "Products" : "Programs"}
            onBack={() => { setSelectedProgramId(null); setActiveNav(programDetailOrigin || "programs"); }}
            _onUpdateStage={handleUpdateProgramStage}
            onUpdateProgram={handleUpdateProgram}
            onDeleteProgram={(id: string | number | undefined) => { handleDeleteProgram(id); setSelectedProgramId(null); setActiveNav(programDetailOrigin || "programs"); }}
            onSave={handleSaveProduct}
            onDelete={handleDeleteProduct}
          />
        </ErrorBoundary>
      );
    }
    switch (activeNav) {
      case "pipeline": return <ErrorBoundary><PipelinePage programs={programsWithCounts} products={products} onOpenProgram={openProgram} /></ErrorBoundary>;
      case "programs": return <ErrorBoundary><ProgramsPage programs={programsWithCounts} products={products} teamMembers={teamMembers} onOpenProgram={openProgram} onCreateProgram={handleCreateProgram} onUpdateProgram={handleUpdateProgram} onDeleteProgram={handleDeleteProgram} onSaveProduct={handleSaveProduct} onDeleteProduct={handleDeleteProduct} /></ErrorBoundary>;
      case "products": return <ErrorBoundary><ProductsPage products={products} programs={programsWithCounts} onSave={handleSaveProduct} onDelete={handleDeleteProduct} onOpenProgram={openProgram} loadMoreProducts={loadMoreProducts} allProductsLoaded={allProductsLoaded} loadingMore={loadingMore} totalProductCount={totalProductCount} currentUser={currentUser} /></ErrorBoundary>;
      case "copy": return <ErrorBoundary><CopyManagerPage products={products} onSaveProduct={handleSaveProduct} appSettings={appSettings} /></ErrorBoundary>;
      case "copy-gen": return <ErrorBoundary><CopyGeneratePage products={products} programs={programsWithCounts} onSaveGenerated={handleSaveGenerated} onSaveProduct={handleSaveProduct} appSettings={appSettings} aiSession={aiSessionRef} loadMoreProducts={loadMoreProducts} allProductsLoaded={allProductsLoaded} loadingMore={loadingMore} totalProductCount={totalProductCount} currentUser={currentUser as any} teamMembers={teamMembers} /></ErrorBoundary>;
      case "studio": return <ErrorBoundary><ImageStudioPage products={products} programs={programsWithCounts} onSaveProduct={handleSaveProduct} appSettings={appSettings} genImagesCache={genImagesCacheRef} aiSession={aiSessionRef} loadMoreProducts={loadMoreProducts} allProductsLoaded={allProductsLoaded} loadingMore={loadingMore} totalProductCount={totalProductCount} currentUser={currentUser as any} teamMembers={teamMembers} /></ErrorBoundary>;
      case "qa": return <ErrorBoundary><QAReviewPage qaQueue={qaQueue} products={products} generatedContent={generatedContent} onDeleteQA={handleDeleteQA} onApproveAndStore={handleApproveAndStoreQA} onSaveGenerated={handleSaveGenerated} appSettings={appSettings} teamMembers={teamMembers} currentUserEmail={currentUser?.email} /></ErrorBoundary>;
      case "settings": return <ErrorBoundary><SettingsPage appSettings={appSettings} updateSettings={updateSettings} saveStatus={settingsSaveStatus} settingsReady={!deferredLoading} /></ErrorBoundary>;
      case "team": return <ErrorBoundary><UserManagementPage teamMembers={teamMembers} onSaveTeamMember={handleSaveTeamMember} currentUser={currentUser as any} session={session} programs={programs} products={products} onlineUsers={onlineUsers} /></ErrorBoundary>;
      default: return <ErrorBoundary><PipelinePage programs={programsWithCounts} products={products} onOpenProgram={openProgram} /></ErrorBoundary>;
    }
  };

  const sidebarWidth = collapsed ? 68 : 220;

  const svgProps = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  const allNavItems: { icon: React.ReactNode; label: string; key: string; match: string[] }[] = [
    { icon: <svg {...svgProps}><rect x="3" y="3" width="7" height="9" rx="1"/><rect x="14" y="3" width="7" height="5" rx="1"/><rect x="14" y="12" width="7" height="9" rx="1"/><rect x="3" y="16" width="7" height="5" rx="1"/></svg>, label: "Pipeline", key: "pipeline", match: ["pipeline", ...(programDetailOrigin === "pipeline" ? ["program-detail"] : [])] },
    { icon: <svg {...svgProps}><path d="M3 7h18M3 12h18M3 17h18"/></svg>, label: "Programs", key: "programs", match: ["programs", ...(programDetailOrigin !== "pipeline" && programDetailOrigin !== "products" ? ["program-detail"] : [])] },
    { icon: <svg {...svgProps}><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="3"/></svg>, label: "Products", key: "products", match: ["products", ...(programDetailOrigin === "products" ? ["program-detail"] : [])] },
    { icon: <svg {...svgProps}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>, label: "Copy Manager", key: "copy", match: ["copy"] },
    { icon: <svg {...svgProps}><path d="M12 2l2.4 7.2H22l-6 4.8 2.4 7.2L12 16.4 5.6 21.2 8 14l-6-4.8h7.6z"/></svg>, label: "Copy Generate", key: "copy-gen", match: ["copy-gen"] },
    { icon: <svg {...svgProps}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>, label: "Image Studio", key: "studio", match: ["studio"] },
    { icon: <svg {...svgProps}><path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="9"/></svg>, label: "QA Review", key: "qa", match: ["qa"] },
  ];
  const navItems = allNavItems.filter(item => canAccess(permissions, item.key, userRole));

  return (
    <div className={`app-layout ff-primary app-layout-transition${darkMode ? " dark-mode" : ""}`}>
      {/* Release notes popup — hidden until explicitly triggered */}

      {/* Global notification toast */}
      {globalMsg && (
        <div className={`toast pointer toast-blur ${globalMsg.type === "error" ? "toast-notify-error" : globalMsg.type === "warn" ? "toast-notify-warn" : "toast-notify-success"}`} onClick={() => setGlobalMsg(null)}>{globalMsg.text}</div>
      )}

      {/* Sidebar */}
      <div className={`flex flex-col flex-none h-full-vh overflow-hidden sidebar-transition py-9 ${darkMode ? "sidebar-bg-dark" : "sidebar-bg-light"}`} style={{ width: sidebarWidth }}>
        {/* Logo + Collapse */}
        <div className="flex items-center justify-between sidebar-logo-area">
          <div onClick={() => window.location.reload()} className="pointer flex items-center overflow-hidden">
            {collapsed ? (
              <img src={darkMode ? "/branding/pxmdash-logo-mark-light.png" : "/branding/pxmdash-logo-mark.png"} alt="PXM Dash" className="logo-mark" />
            ) : (
              <img src={darkMode ? "/branding/pxmdash-logo-horizontal-light.png" : "/branding/pxmdash-logo-horizontal.png"} alt="PXM Dash" className="logo-horizontal" />
            )}
          </div>
          {!collapsed && (
            <button onClick={() => { const next = !collapsed; setCollapsed(next); localStorage.setItem("pxm_sidebar_collapsed", String(next)); }} title="Collapse" className="flex-center no-border pointer flex-none sidebar-collapse-btn sidebar-collapse-btn-light">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          )}
          {collapsed && (
            <button onClick={() => { const next = !collapsed; setCollapsed(next); localStorage.setItem("pxm_sidebar_collapsed", String(next)); }} title="Expand" className="flex-center no-border pointer flex-none sidebar-collapse-btn sidebar-collapse-btn-collapsed sidebar-collapse-btn-light">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="rotate-180">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          )}
        </div>

        {/* Nav items */}
        <div className="flex-col w-full">
          {navItems.map(item => {
            const isActive = item.match.includes(activeNav);
            return (
              <button key={item.key} onClick={() => setActiveNav(item.key)} title={collapsed ? item.label : undefined} className={`sidebar-nav-item${isActive ? " active" : ""}${!darkMode ? " light" : ""}`}>
                <span className="flex-none flex-center size-18">{item.icon}</span>
                <span className={collapsed ? "nav-label-hide" : "nav-label-show"}>{item.label}</span>
              </button>
            );
          })}
        </div>

        {/* Settings */}
        {/* Bottom section — settings + theme */}
        <div className="w-full mt-6 pt-5 sidebar-divider-light">
          {canAccess(permissions, "settings", userRole) && (
            <button onClick={() => setActiveNav("settings")} title={collapsed ? "Settings" : undefined} className={`sidebar-nav-item${activeNav === "settings" ? " active" : ""}${!darkMode ? " light" : ""}`}>
              <span className="flex-none flex-center size-18"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg></span>
              <span className={collapsed ? "nav-label-hide" : "nav-label-show"}>Settings</span>
            </button>
          )}
          {/* Dark mode toggle */}
          <button onClick={() => setDarkMode(!darkMode)} title={collapsed ? (darkMode ? "Light mode" : "Dark mode") : undefined} className={`sidebar-nav-item${!darkMode ? " light" : ""}`}>
            <span className="flex-none flex-center size-18">
              {darkMode ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>
              )}
            </span>
            <span className={collapsed ? "nav-label-hide" : "nav-label-show"}>{darkMode ? "Light Mode" : "Dark Mode"}</span>
          </button>
        </div>

        <div className="flex-1" />

        {/* User profile */}
        <div onClick={() => canAccess(permissions, "team", userRole) && setActiveNav("team")} title={collapsed ? `${currentUser?.name || "User"} · Team` : undefined} className="flex items-center gap-3 pointer sidebar-user-area sidebar-user-divider-light">
          <MemberAvatar
            member={teamMembers.find(m => m.email === currentUser?.email || m.name === currentUser?.name)}
            name={currentUser?.name}
          />
          <div className={`overflow-hidden flex-1 min-w-0 ${collapsed ? "nav-label-hide" : "nav-label-show"}`}>
            <div className="text-sm fw-600 ff-primary truncate text-dark-dm-white">{currentUser?.name || "User"}</div>
            <div className="text-xs ff-secondary truncate text-light-dm-dim">{currentUser?.title || currentUser?.email || ""}</div>
          </div>
        </div>

        {/* Logout & Version */}
        <div className="flex items-center sidebar-bottom-area nowrap">
          {onLogout && (
            <button onClick={(e) => { e.stopPropagation(); onLogout(); }} title="Sign Out" className="flex items-center gap-2 no-border bg-transparent pointer text-xs ff-secondary flex-none text-light-dm-muted">
              <div className="flex-center flex-none size-28">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              </div>
              <span className={collapsed ? "nav-label-hide" : "nav-label-show"}>Sign Out</span>
            </button>
          )}
          <span className={`text-xs ff-secondary ml-auto text-light-dm-faint ${collapsed ? "version-fade-hide" : "version-fade-show"}`}>{DEV_VERSION}</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content slide-up main-bg-transition">
        <div className="page-content-wrap">
          {deferredLoading && (
            <div className="flex items-center gap-3 mb-5 rounded deferred-loading-bar">
              <div className="rounded-full animate-spin flex-none deferred-spinner" />
              <span className="text-xs ff-secondary text-mid">Syncing details...</span>
            </div>
          )}
          <ErrorBoundary>
            <Suspense fallback={<div className="flex-center suspense-fallback"><span className="text-light text-sm">Loading...</span></div>}>
              {renderContent()}
            </Suspense>
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
}
